import { DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm } from 'antd';
import { useFormikContext } from 'formik';
import { isFinite, isNil, orderBy } from 'lodash/fp';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '../..';
import { FinanceProductBasedOn } from '../../../../../../api/types';
import CollapsibleWrapper, { Panel } from '../../../../../../components/wrappers/CollapsibleWrapper';
import { useFinanceProductDetailContext } from '../../../FinanceProductDetailContext';
import AddRow from '../../../shared/AddRow';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import EditableTable, { type Columns } from '../EditableTable';
import { type TableRecord, type TableProps } from './types';

const RandomTable = ({ disabled = false, onExport, mappedHeaders }: TableProps) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const {
        basedOn,
        interestRate: { table: interestRateTable },
    } = values;

    const { bank } = useFinanceProductDetailContext();
    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);

    const [records, setRecords] = useState<TableRecord[]>([]);

    const onUpdate = useCallback(
        (record: TableRecord) => {
            const { key: targetKey } = record;
            const index = records.findIndex(({ key }) => key === targetKey);

            // clone current records
            const current = [...records];
            // update target
            current.splice(index, 1, record);

            setRecords(current);

            setFieldValue(
                'interestRate.table',
                current
                    .filter(({ value, base, term }) => isFinite(value) || isFinite(base) || isFinite(term))
                    .map(({ term, base, value }) => ({ term, base, value }))
            );
        },
        [records, setFieldValue]
    );

    const onDelete = useCallback(
        row => {
            const current = records.filter(i => i.key !== row.key);
            setRecords(records.filter(i => i.key !== row.key));

            setFieldValue(
                'interestRate.table',
                current
                    .filter(({ value, base, term }) => isFinite(value) || isFinite(base) || isFinite(term))
                    .map(({ term, base, value }) => ({ term, base, value }))
            );
        },
        [records, setFieldValue]
    );

    const columns = useMemo<Columns<TableRecord>>(
        () => [
            {
                title: t('financeProductDetails:tables.term.title'),
                dataIndex: 'term',
                fixed: true,
                onCell: record => ({ record, dataIndex: 'term', isEditable: !disabled, onUpdate }),
            },
            {
                title: (
                    <>
                        {t(
                            `financeProductDetails:tables.interestRate.columnTitles.${
                                basedOn === FinanceProductBasedOn.DownPayment ? 'downPaymentTitle' : 'loanTitle'
                            }`
                        )}{' '}
                        {amountUnitOptions.find(item => item.value === values?.interestRate?.unit)?.label}
                    </>
                ),
                dataIndex: 'base',
                onCell: record => ({ record, dataIndex: 'base', isEditable: !disabled, onUpdate }),
            },
            {
                title: t('financeProductDetails:tables.interestRate.title'),
                dataIndex: 'value',
                onCell: record => ({
                    record,
                    dataIndex: 'value',
                    isEditable: !disabled,
                    onUpdate,
                }),
            },
            {
                title: '',
                dataIndex: 'delete',
                align: 'center',
                render: (_value, row) => (
                    <Popconfirm
                        cancelText={t('financeProductDetails:deleteRowModal.cancelText')}
                        okText={t('financeProductDetails:deleteRowModal.okText')}
                        onConfirm={() => onDelete(row)}
                        title={t('financeProductDetails:deleteRowModal.title')}
                    >
                        <Button icon={<DeleteOutlined />} type="link" danger />
                    </Popconfirm>
                ),
            },
        ],
        [t, basedOn, amountUnitOptions, disabled, onUpdate, values?.interestRate?.unit, onDelete]
    );

    const onAdd = useCallback(value => {
        setRecords(state => [
            ...state,
            ...Array.from({ length: value }, () => ({
                key: nanoid(),
                term: undefined,
                base: undefined,
                value: undefined,
            })),
        ]);
    }, []);

    const afterImport = useRef(false);

    // init data interestRateTable => record
    useEffect(() => {
        const newRecords = [];
        // after importing, need to replace previous table data
        const currents = afterImport.current ? [] : [...records];
        interestRateTable.forEach(i => {
            const find = currents.find(
                record => i.term === record.term && i.base === record.base && i.value === record.value
            );

            if (!find) {
                newRecords.push({
                    ...i,
                    key: nanoid(),
                });
            }
        });

        setRecords(
            afterImport.current || currents.length === 0
                ? orderBy('term', 'asc', [...currents, ...newRecords])
                : [...currents, ...newRecords]
        );
        afterImport.current = false;
    }, [interestRateTable]);

    const onImport = useCallback(
        async data => {
            const tableDataList = data
                .filter(record => !isNil(record.value) && parseFloat(record.value) >= 0)
                .map(value => ({
                    term: value.term,
                    base: value.base,
                    value: parseFloat(value.value),
                }));
            setFieldValue('interestRate.table', tableDataList);
            afterImport.current = true;
        },
        [setFieldValue]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={['interestRate']}>
            <Panel key="interestRate" header={t('financeProductDetails:tables.interestRate.title')}>
                <EditableTable columns={columns} records={records} />
            </Panel>

            <ImportExcel
                mappedHeaders={mappedHeaders}
                onImport={onImport}
                tableType="matrix"
                title={t('financeProductDetails:importModal.titles.interestRate')}
            />
            <ExportExcel onClick={() => onExport(records)} />
            <AddRow onAdd={onAdd} />
        </CollapsibleWrapper>
    );
};

export default RandomTable;
